/**
 * STAFF_PAGES — single source of truth for every admin-panel section
 * that can be granted to a staff member.
 *
 * Used by:
 *  • AdminStaff.js    — permissions checkbox grid in Create/Edit modal
 *  • AdminPage.js     — sidebar filtering + route-level access guard
 *  • backend/routes/staff.js — VALID_PAGE_KEYS (kept in sync manually)
 *
 * Each entry:
 *  key          unique string matching the backend's allowedPages values
 *  label        human-readable name shown in the checkbox grid
 *  group        section heading in the checkbox grid
 *  adminOnly    if true, this page is NEVER shown to staff regardless of permissions
 */
export const STAFF_PAGES = [
  // ── Operations ─────────────────────────────────────────────────
  { key: 'sales-orders',      label: 'Sales Orders',      group: 'Operations' },
  { key: 'order-detail',      label: 'Order Detail',      group: 'Operations' },
  { key: 'production',        label: 'Production',        group: 'Operations' },
  { key: 'delivered',         label: 'Delivered',         group: 'Operations' },
  { key: 'quote-requests',    label: 'Quote Requests',    group: 'Operations' },
  { key: 'quotations',        label: 'Quotations',        group: 'Operations' },

  // ── Finance ────────────────────────────────────────────────────
  { key: 'payments',          label: 'Receipts',          group: 'Finance' },
  { key: 'billing',           label: 'Tax Invoice',       group: 'Finance' },
  { key: 'purchases',         label: 'Purchases',         group: 'Finance' },
  { key: 'expenses',          label: 'Expenses',          group: 'Finance' },
  { key: 'expense-categories',label: 'Expense Categories',group: 'Finance' },
  { key: 'ledger',            label: 'Ledger',            group: 'Finance' },
  { key: 'tds',               label: 'TDS Register',      group: 'Finance' },

  // ── CRM ────────────────────────────────────────────────────────
  { key: 'users',             label: 'Users',             group: 'CRM' },
  { key: 'businesses',        label: 'Businesses',        group: 'CRM' },
  { key: 'contacts',          label: 'Messages',          group: 'CRM' },

  // ── Vendors ────────────────────────────────────────────────────
  { key: 'vendors',           label: 'All Vendors',       group: 'Vendors' },
  { key: 'vendor-types',      label: 'Vendor Types',      group: 'Vendors' },
  { key: 'vendor-payments',   label: 'Vendor Payments',   group: 'Vendors' },
  { key: 'services',          label: 'Services',          group: 'Vendors' },

  // ── Products ───────────────────────────────────────────────────
  { key: 'products',          label: 'Products',          group: 'Products' },
  { key: 'catalog',           label: 'Categories',        group: 'Products' },
  { key: 'search-analytics',  label: 'Search Analytics',  group: 'Products' },

  // ── Website ────────────────────────────────────────────────────
  { key: 'public-services',   label: 'Public Services',   group: 'Website' },
  { key: 'client-logos',      label: 'Our Clients',       group: 'Website' },
  { key: 'about-us',          label: 'About Us',          group: 'Website' },
  { key: 'contact-hero',      label: 'Contact Hero',      group: 'Website' },

  // ── Settings (admin-only — kept here for completeness but blocked for staff) ──
  { key: 'payment-terms',     label: 'Payment Terms',     group: 'Settings', adminOnly: true },
  { key: 'bank-accounts',     label: 'Bank Accounts',     group: 'Settings', adminOnly: true },
  { key: 'site-settings',     label: 'Site Settings',     group: 'Settings', adminOnly: true },
  { key: 'email-settings',    label: 'Email Settings',    group: 'Settings', adminOnly: true },
];

/** Pages that staff can be granted (excludes adminOnly entries) */
export const GRANTABLE_PAGES = STAFF_PAGES.filter(p => !p.adminOnly);

/** All unique group names in display order */
export const PAGE_GROUPS = [...new Set(STAFF_PAGES.filter(p => !p.adminOnly).map(p => p.group))];

/**
 * Given a staff user's allowedPages array, check if they can access a page.
 * mode: 'view' (default) | 'edit'
 */
export function canStaffAccess(allowedPages, pageKey, mode = 'view') {
  if (!Array.isArray(allowedPages)) return false;
  return allowedPages.some(p => {
    if (p === pageKey) return true; // bare key = full/edit access
    if (mode === 'view') return p === `${pageKey}:view` || p === `${pageKey}:edit`;
    if (mode === 'edit') return p === `${pageKey}:edit`;
    return false;
  });
}

/**
 * Map a sidebar path to its page key.
 * E.g. "/admin/billing" → "billing"
 */
export function pathToKey(path) {
  return path.replace(/^\/admin\/?/, '').split('/')[0] || 'dashboard';
}
